import { defineStore } from 'pinia';
import { computed, reactive, ref, toRefs, watch } from 'vue';
import { watchImmediate } from '@vueuse/core';
import { taggedLogger } from '@/util/taggedLogger';
import { usePlannerStore } from '@/views/3d-planner/store/plannerStore';
import {
    type PlannerMode,
    type PlannerViewState,
    plannerViewState,
} from '@/views/3d-planner/store/plannerState';
import type { PlannerStore } from '@/views/3d-planner/store/plannerStore.types';
import {
    plannerSceneState,
    type PlannerSceneState,
} from '@/views/3d-planner/scene/plannerSceneState';
import {
    initialPlannerCatstacksState,
    plannerCatstacksState,
} from '@/views/3d-planner/store/catstacksState';
import {
    initialVisibilityToggles,
    type VisibilityToggleId,
} from '@/views/3d-planner/measurements/visibilityToggles';
import { crossSectionIds, isCrossSectionId } from '@/views/3d-planner/scene/crossSection';
import type {
    PlannerSceneContext,
    PlannerSceneViewModel,
} from '@/views/3d-planner/store/plannerSceneViewModel.types';

const log = taggedLogger('planner-scene');

// ─── Derived state ────────────────────────────────────────────────────────────

export function createPlannerSceneComputed({ sceneState }: PlannerSceneContext) {
    return {
        nodes: computed(() => sceneState.value.nodes),
        rendering: computed(() => sceneState.value.rendering),
    };
}

// ─── Mutations ──────────────────────────────────────────────────────────────

/** Planner-mode and scene-visibility mutations. */
export function viewActions({ view }: PlannerSceneContext) {
    return {
        setMode: (mode: PlannerMode) => {
            log.info('Planner mode set to %s', mode);
            view.plannerMode = mode;
            view.visibility = initialVisibilityToggles(mode);
        },
        toggleVisibility: (id: VisibilityToggleId, on: boolean) => {
            if (isCrossSectionId(id) && on) {
                // Toggling a cross-section on toggles other visible cross-sections off
                const ids = crossSectionIds.filter(
                    (id_) => id_ !== id && view.visibility[id_] === 'on',
                );
                ids.forEach((id_) => (view.visibility[id_] = 'off'));
            }
            view.visibility[id] = on ? 'on' : 'off';
        },
    };
}

// ─── Reactions to the Model ───────────────────────────────────────────────────

/**
 * Project domain state onto presentation state, one-way (Model → VM). The scene nodes themselves
 * are projected by the scene-update layer; here we own only the view-state reactions:
 * - reset presentation state when the loaded case clears;
 * - mode transitions following operation completion (mirrors the old in-operation mode writes);
 * - catstack slice-bounds initialised from domain data when it lands.
 */
function registerSceneViewWatchers(model: PlannerStore, view: PlannerViewState): void {
    watch(
        () => model.case?.caseId ?? null,
        (id) => {
            if (id === null) {
                view.plannerMode = 'disabled';
                view.renderingMode = 'normal';
                view.visibility = initialVisibilityToggles('default');
            }
        },
    );

    watch(
        () => model.currentOperation,
        (op, prev) => {
            if (op !== null) return;
            if (prev === 'load-case' && model.case) view.plannerMode = 'default';
            else if (prev === 'set-targets') view.plannerMode = 'stem';
        },
    );

    watchImmediate(
        () => model.catstacksData,
        (data) => {
            view.catstacks =
                data && model.fittedComponents && model.template
                    ? plannerCatstacksState(data, model.fittedComponents, model.template.stemUrl)
                    : initialPlannerCatstacksState();
        },
    );

    // First-load safety net: a case may already be loaded by the time the scene VM mounts (the
    // viewport mounts after PlannerView kicks off the load), so the operation watcher above would
    // have missed the completion.
    if (!model.isLoading && model.case !== null) {
        view.plannerMode = 'default';
    }
}

/** The scene ViewModel for the 3D-planning view. */
export const usePlannerSceneViewModel: () => PlannerSceneViewModel = defineStore(
    'planner-scene',
    () => {
        const model = usePlannerStore();
        const view = reactive<PlannerViewState>(plannerViewState());
        const sceneState = ref<PlannerSceneState>(plannerSceneState());

        // Rebuild the scene graph for each case (as the original design did). The View calls this at
        // the start of a load, before the viewport mounts, so the new SceneContext captures the fresh
        // graph. A fresh graph is required rather than reusing the previous case's nodes because
        // SceneContext.removeNode reparents children to the scene root on teardown, orphaning them.
        function resetScene(): void {
            sceneState.value = plannerSceneState();
        }

        const ctx: PlannerSceneContext = {
            state: model,
            view,
            sceneState,
        };

        registerSceneViewWatchers(model, view);

        return {
            sceneState,
            resetScene,
            ...toRefs(view),
            ...createPlannerSceneComputed(ctx),
            ...viewActions(ctx),
        };
    },
);